// Hardcoded TypeRail / TypeMenu data.
// Stage lists hold featured names per subcategory; clicking one runs a
// search for that name (/listings/:searchTerm), which matches any card whose
// name CONTAINS it — so "Jirachi" also finds "Jirachi ◆" and "Shining Jirachi".
//
// The Pokémon, Trainers, Energies and Promos names were picked from a snapshot
// of active listings (Oct 2026), most-listed first, so every link returns
// results. Re-check them when inventory turns over. Avoid one- or two-letter
// names (e.g. "N"): a contains-match on those hits nearly every card.
// Replace with an API call (e.g. GET /featured?type=water&stage=basic) when the
// backend supports it.

// Where a featured name links: a set page, an artist page, or a name search.
export const featuredPath = (type, name) => {
    if (type.linkKind === 'set') {
        // Match SetsPage's slug logic so links hit the same set route.
        const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        return `/sets/${slug}`;
    }
    if (type.linkKind === 'artist') return `/artists/${encodeURIComponent(name)}`;
    return `/listings/${encodeURIComponent(name)}`;
};

// Where a subcategory links: the listings page filtered by subtype (Pokémon,
// Trainers, Energies) or by series (Sets, Promos). Null when the type has no
// browse page for its subcategories (Artists).
export const subcategoryPath = (type, stage) => {
    const p = new URLSearchParams();
    if (type.supertype) {
        p.set('supertype', type.supertype);
        if (stage.subtype) p.set('subtype', stage.subtype);
    } else if (type.browseBy === 'series') {
        p.set('series', stage.label);
        if (type.isPromo) p.set('isPromo', '1');
    } else {
        return null;
    }
    return `/listings?${p.toString()}`;
};

export const STAGES = [
    { id: 'stage2', label: 'Stage 2' },
    { id: 'stage1', label: 'Stage 1' },
    { id: 'basic', label: 'Basic' },
];

export const TYPES = [
    {
        // The Pokémon supertype uses the standard evolution stages.
        // Clicking a subcategory browses /listings?supertype=pokemon&subtype=…
        id: 'pokemon', label: 'Pokémon', color: '#e3b514',
        supertype: 'pokemon',
        route: '/pokemon',
        stageList: [
            { id: 'stage2', label: 'Stage 2', subtype: 'Stage 2' },
            { id: 'stage1', label: 'Stage 1', subtype: 'Stage 1' },
            { id: 'basic', label: 'Basic', subtype: 'Basic' },
        ],
        stages: {
            stage2: ['Charizard', 'Alakazam', 'Blastoise', 'Sceptile', 'Kingdra', 'Gengar', 'Metagross', 'Dusknoir'],
            stage1: ['Lucario', 'Magcargo', 'Marowak', 'Malamar', 'Banette', 'Gallade', 'Manectric', "Rocket's Persian"],
            basic: ['Jirachi', 'Xerneas', 'Giratina', 'Volcanion', 'Iron Thorns', 'Latias', 'Tornadus', 'Pikachu'],
        },
    },
    {
        // Trainers aren't a Pokémon type, so they carry their own subcategory
        // list (Supporters / Stadiums / Items / Tools) instead of the stages.
        id: 'trainers', label: 'Trainers', color: '#b07b2e',
        supertype: 'trainer',
        route: '/trainer',
        stageList: [
            { id: 'supporters', label: 'Supporters', subtype: 'Supporter' },
            { id: 'stadiums', label: 'Stadiums', subtype: 'Stadium' },
            { id: 'items', label: 'Items', subtype: 'Item' },
            { id: 'tools', label: 'Tools', subtype: 'Pokémon Tool' },
        ],
        stages: {
            supporters: ['Winona', 'Iono', 'Arven', 'Lusamine', 'Hex Maniac', 'Skyla', 'Cheren', 'Karen'],
            stadiums: ['Heat Factory', 'Thunder Mountain', 'Life Forest'],
            items: ['Secret Box', 'Mysterious Treasure', 'Energy Switch', 'Rare Candy', 'Quick Ball', 'Nest Ball', 'Dowsing Machine', 'Unfair Stamp'],
            tools: ['Escape Board', 'Forest Seal Stone', 'Rock Guard', 'G Booster', 'G Scope'],
        },
    },
    {
        // Energies carry their own subtypes (Special / Basic) rather than stages.
        // The Basic names aren't from the listings snapshot (it had no Basic
        // Energy), so they may come up empty until some are listed.
        id: 'energy', label: 'Energies', color: '#4aa36b',
        supertype: 'energy',
        route: '/energy',
        stageList: [
            { id: 'special', label: 'Special', subtype: 'Special' },
            { id: 'basic', label: 'Basic', subtype: 'Basic' },
        ],
        stages: {
            special: ['Beast Energy', 'Counter Energy', 'Legacy Energy', 'Luminous Energy', 'Reversal Energy', 'Super Boost Energy'],
            basic: ['Grass Energy', 'Fire Energy', 'Water Energy', 'Lightning Energy', 'Psychic Energy', 'Fighting Energy', 'Darkness Energy', 'Metal Energy'],
        },
    },
    {
        // Promos grouped by era. Clicking a name searches ALL listings by name
        // (not just promos), so these are listed names from each era.
        id: 'promos', label: 'Promos', color: '#d1457f', browseBy: 'series', isPromo: true, route: '/promo',
        stageList: [
            { id: 'sv', label: 'Scarlet & Violet' },
            { id: 'swsh', label: 'Sword & Shield' },
            { id: 'sm', label: 'Sun & Moon' },
        ],
        stages: {
            sv: ['Iron Thorns', 'Charizard', 'Iono', 'Arven', 'Eevee', 'Skarmory'],
            swsh: ['Mew', 'Pikachu', 'Slaking', 'Alakazam', 'Nessa', 'Lucario'],
            sm: ['Buzzwole', 'Tapu Lele', 'Lunala', 'Lusamine', 'Ilima', 'Lana'],
        },
    },
    {
        // Sets: subcategories are series, items are set names. linkKind 'set'
        // makes clicks open /sets/:slug instead of running a search.
        id: 'sets', label: 'Sets', color: '#5a6b85', linkKind: 'set', browseBy: 'series', route: '/sets',
        stageList: [
            { id: 'sv', label: 'Scarlet & Violet' },
            { id: 'swsh', label: 'Sword & Shield' },
            { id: 'sm', label: 'Sun & Moon' },
        ],
        stages: {
            sv: ['151', 'Obsidian Flames', 'Paldea Evolved', 'Paradox Rift', 'Temporal Forces', 'Surging Sparks'],
            swsh: ['Brilliant Stars', 'Evolving Skies', 'Lost Origin', 'Crown Zenith', 'Fusion Strike', 'Astral Radiance'],
            sm: ['Hidden Fates', 'Cosmic Eclipse', 'Unified Minds', 'Lost Thunder', 'Celestial Storm', 'Team Up'],
        },
    },
    {
        // Artists: same era blocks as Sets; items are illustrator names. linkKind
        // 'artist' opens /artists/:name (filtered by artistName). Names are
        // hardcoded suggestions and must match the catalog's artist strings
        // exactly to return results.
        id: 'artist', label: 'Artists', color: '#7a5bb0', linkKind: 'artist', browseBy: 'series',
        stageList: [
            { id: 'sv', label: 'Scarlet & Violet' },
            { id: 'swsh', label: 'Sword & Shield' },
            { id: 'sm', label: 'Sun & Moon' },
        ],
        stages: {
            sv: ['5ban Graphics', 'PLANETA Mochizuki', 'Saki Hayashiro', 'Mitsuhiro Arita', 'aky CG Works', 'Ryo Ueda'],
            swsh: ['5ban Graphics', 'Ryota Murayama', 'Tomokazu Komiya', 'saino misaki', 'Naoyo Kimura', 'PLANETA Tsuji'],
            sm: ['Shin Nagasawa', '5ban Graphics', 'Mitsuhiro Arita', 'Atsuko Nishida', 'Yuka Morii', 'Kagemaru Himeno'],
        },
    },
    {
        id: 'water', label: 'Water', color: '#3d9ad1',
        stages: {
            stage2: ['Blastoise', 'Feraligatr', 'Swampert', 'Greninja', 'Poliwrath', 'Kingdra'],
            stage1: ['Gyarados', 'Vaporeon', 'Golduck', 'Wartortle', 'Seadra', 'Starmie'],
            basic: ['Squirtle', 'Lapras', 'Magikarp', 'Psyduck', 'Staryu', 'Totodile'],
        },
    },
    {
        id: 'fire', label: 'Fire', color: '#e8633a',
        stages: {
            stage2: ['Charizard', 'Typhlosion', 'Blaziken', 'Infernape', 'Delphox'],
            stage1: ['Arcanine', 'Ninetales', 'Flareon', 'Charmeleon', 'Magmar'],
            basic: ['Charmander', 'Vulpix', 'Growlithe', 'Ponyta', 'Cyndaquil', 'Moltres'],
        },
    },
    {
        id: 'electric', label: 'Electric', color: '#e3b514',
        stages: {
            stage2: ['Ampharos', 'Magnezone', 'Electivire', 'Luxray'],
            stage1: ['Raichu', 'Jolteon', 'Electrode', 'Magneton', 'Flaaffy'],
            basic: ['Pikachu', 'Electabuzz', 'Voltorb', 'Magnemite', 'Zapdos', 'Mareep'],
        },
    },
    {
        id: 'psychic', label: 'Psychic', color: '#9c59b8',
        stages: {
            stage2: ['Alakazam', 'Gardevoir', 'Gengar', 'Reuniclus'],
            stage1: ['Kadabra', 'Haunter', 'Espeon', 'Kirlia', 'Drowzee'],
            basic: ['Mewtwo', 'Mew', 'Abra', 'Ralts', 'Gastly', 'Jynx'],
        },
    },
    {
        id: 'dragon', label: 'Dragon', color: '#5f66d6',
        stages: {
            stage2: ['Dragonite', 'Haxorus', 'Goodra', 'Hydreigon', 'Salamence'],
            stage1: ['Dragonair', 'Fraxure', 'Sliggoo', 'Shelgon'],
            basic: ['Rayquaza', 'Dratini', 'Axew', 'Goomy', 'Bagon', 'Latias'],
        },
    },
    {
        id: 'fairy', label: 'Fairy', color: '#d9699f',
        stages: {
            stage2: ['Togekiss', 'Gardevoir'],
            stage1: ['Clefable', 'Wigglytuff', 'Togetic', 'Sylveon', 'Granbull'],
            basic: ['Clefairy', 'Jigglypuff', 'Togepi', 'Xerneas', 'Snubbull'],
        },
    },
    {
        id: 'dark', label: 'Dark', color: '#3f4454',
        stages: {
            stage2: ['Tyranitar', 'Hydreigon'],
            stage1: ['Umbreon', 'Weavile', 'Zoroark', 'Honchkrow', 'Mightyena'],
            basic: ['Absol', 'Sneasel', 'Murkrow', 'Zorua', 'Darkrai', 'Poochyena'],
        },
    },
    {
        id: 'metal', label: 'Metal', color: '#7a8699',
        stages: {
            stage2: ['Metagross', 'Aggron', 'Empoleon'],
            stage1: ['Scizor', 'Steelix', 'Metang', 'Lairon', 'Bronzong'],
            basic: ['Registeel', 'Beldum', 'Aron', 'Jirachi', 'Cobalion'],
        },
    },
    {
        id: 'colorless', label: 'Colorless', color: '#9aa0ab',
        stages: {
            stage2: ['Pidgeot', 'Stoutland', 'Staraptor'],
            stage1: ['Dodrio', 'Fearow', 'Furret', 'Chansey'],
            basic: ['Eevee', 'Snorlax', 'Ditto', 'Tauros', 'Kangaskhan', 'Lugia'],
        },
    },
    {
        id: 'fighting', label: 'Fighting', color: '#b4653c',
        stages: {
            stage2: ['Machamp', 'Conkeldurr', 'Lucario'],
            stage1: ['Machoke', 'Primeape', 'Hariyama', 'Gurdurr'],
            basic: ['Machop', 'Riolu', 'Hitmonchan', 'Hitmonlee', 'Mankey', 'Terrakion'],
        },
    },
    {
        id: 'grass', label: 'Grass', color: '#55a344',
        stages: {
            stage2: ['Venusaur', 'Meganium', 'Sceptile', 'Vileplume', 'Butterfree'],
            stage1: ['Ivysaur', 'Bayleef', 'Grovyle', 'Gloom', 'Metapod'],
            basic: ['Bulbasaur', 'Chikorita', 'Treecko', 'Oddish', 'Caterpie', 'Celebi'],
        },
    },
];

// The categories shown in the desktop side rail and the mobile hamburger menu:
// Pokémon, Trainers, Energies, Promos, Sets, Artists. (The type entries after
// these aren't shown anywhere yet.)
export const RAIL_TYPES = TYPES.slice(0, 6);
